import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AuditLoadingBrain } from "./AuditLoadingBrain";
import { CriticalFindingsAlert } from "./CriticalFindingsAlert";
import { ActionPlanChecklist } from "./ActionPlanChecklist";
import {
  Brain,
  TrendingUp,
  DollarSign,
  Clock,
  Mail,
  ClipboardCheck,
  History,
  Calendar,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Target,
  Loader2
} from "lucide-react";

interface CriticalFindingItem {
  id: string;
  name: string;
  value?: number;
  assignedTo?: string;
  lastContact?: string;
  link?: string;
}

interface CriticalFinding {
  type: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  value: number;
  actionable: string;
  items?: CriticalFindingItem[];
}

interface AuditMetrics {
  overview: {
    totalJobs: number;
    completedJobs: number;
    activeJobs: number;
    totalLeads: number;
    healthScore: number;
  };
  revenue: {
    totalRevenue: number;
    avgJobValue: number;
    lostRevenue: number;
  };
  performance: {
    avgResponseTime: number;
    conversionRate: number;
    leadCompletenessScore: number;
    jobCompletenessScore: number;
  };
  campaigns: {
    totalSent: number;
    openRate: number;
    clickRate: number;
    bounceRate: number;
  };
  tasks: {
    totalTasks: number;
    completedTasks: number;
    overdueTasks: number;
    taskCompletionRate: number;
  };
  pipeline: {
    stages: Array<{ name: string; count: number }>;
    staleLeads: number;
  };
  team: {
    totalMembers: number;
    userPerformance: any[];
  };
}

interface AuditResult {
  metrics: AuditMetrics;
  auditReport: string;
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  recommendations: string[];
  healthScore: number;
  criticalFindings: CriticalFinding[];
  auditId?: string;
  generatedAt: string;
}

interface ActionPlan {
  summary: string;
  goals: Array<{
    title: string;
    currentMetric?: string;
    targetMetric?: string;
    priority?: string;
    checklistItems: Array<{
      task: string;
      description?: string;
      estimatedTime?: string;
      done: boolean;
    }>;
  }>;
  quickWins: Array<{
    task: string;
    impact?: string;
    done: boolean;
  }>;
  weeklyMilestones: any[];
}

interface AuditHistory {
  id: string;
  created_at: string;
  overall_score: number;
}

export function AuditTab() {
  const [isRunningAudit, setIsRunningAudit] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [actionPlan, setActionPlan] = useState<ActionPlan | null>(null);
  const [actionPlanId, setActionPlanId] = useState<string | null>(null);
  const [goals, setGoals] = useState("");
  const [auditHistory, setAuditHistory] = useState<AuditHistory[]>([]);
  const [auditProgress, setAuditProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState("");
  const [showGoalInput, setShowGoalInput] = useState(false);
  const [planProgress, setPlanProgress] = useState(0);
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  useEffect(() => {
    loadAuditHistory();
  }, []);

  const loadAuditHistory = async () => {
    const { data, error } = await supabase
      .from("crm_audits")
      .select("id, created_at, overall_score")
      .order("created_at", { ascending: false })
      .limit(5);

    if (error) {
      console.error("Error loading audit history:", error);
      return;
    }

    setAuditHistory((data || []) as AuditHistory[]);
  };

  const loadAuditById = async (auditId: string) => {
    setIsLoadingAudit(true);
    try {
      const { data, error } = await supabase
        .from("crm_audits")
        .select("*")
        .eq("id", auditId)
        .single();

      if (error) throw error;

      // Transform DB data to AuditResult format
      const loadedAudit: AuditResult = {
        metrics: data.metrics as unknown as AuditMetrics,
        auditReport: data.audit_report,
        strengths: data.strengths || [],
        weaknesses: data.weaknesses || [],
        opportunities: data.opportunities || [],
        recommendations: data.recommendations || [],
        healthScore: data.overall_score,
        criticalFindings: (data.critical_findings as unknown as CriticalFinding[]) || [],
        auditId: data.id,
        generatedAt: data.created_at,
      };

      setAuditResult(loadedAudit);
      setSelectedAuditId(auditId);
      toast.success("Audit loaded");
    } catch (error: any) {
      console.error("Error loading audit:", error);
      toast.error("Failed to load audit");
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const backToLatest = () => {
    setSelectedAuditId(null);
    if (auditHistory.length > 0) {
      loadAuditById(auditHistory[0].id);
    }
  };

  const runAudit = async () => {
    setIsRunningAudit(true);
    setAuditProgress(0);
    setCurrentTask("Initializing deep analysis...");

    try {
      const progressSteps = [
        { progress: 10, task: "Analyzing user performance..." },
        { progress: 25, task: "Detecting stale leads..." },
        { progress: 40, task: "Calculating data completeness..." },
        { progress: 55, task: "Identifying pipeline bottlenecks..." },
        { progress: 70, task: "Analyzing lost revenue..." },
        { progress: 85, task: "Running AI analysis..." },
      ];

      let currentStep = 0;
      const progressInterval = setInterval(() => {
        if (currentStep < progressSteps.length) {
          const step = progressSteps[currentStep];
          setAuditProgress(step.progress);
          setCurrentTask(step.task);
          currentStep++;
        }
      }, 1000);

      const { data, error } = await supabase.functions.invoke("run-crm-audit", {
        body: {},
      });

      clearInterval(progressInterval);
      setAuditProgress(100);
      setCurrentTask("Complete!");

      if (error) throw error;

      await new Promise(resolve => setTimeout(resolve, 500));

      setAuditResult(data);
      loadAuditHistory();
      toast.success("Deep audit completed!");
    } catch (error: any) {
      console.error("Error running audit:", error);
      toast.error(error.message || "Failed to run audit");
    } finally {
      setIsRunningAudit(false);
      setAuditProgress(0);
      setCurrentTask("");
    }
  };

  const generateActionPlan = async () => {
    if (!goals.trim()) {
      toast.error("Please enter your goals first");
      return;
    }

    setIsGeneratingPlan(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-action-plan", {
        body: {
          goals,
          currentMetrics: auditResult?.metrics,
          auditInsights: auditResult
            ? `Strengths: ${auditResult.strengths.join(', ')}\nWeaknesses: ${auditResult.weaknesses.join(', ')}`
            : undefined,
          criticalFindings: auditResult?.criticalFindings,
          auditId: auditResult?.auditId,
        },
      });

      if (error) throw error;

      setActionPlan(data.actionPlan);
      setActionPlanId(data.planId);
      setPlanProgress(0);
      setShowGoalInput(false);
      toast.success("Action plan generated!");
    } catch (error: any) {
      console.error("Error generating plan:", error);
      toast.error(error.message || "Failed to generate action plan");
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const getSuggestedGoals = () => {
    if (!auditResult?.criticalFindings || auditResult.criticalFindings.length === 0) {
      return [];
    }
    return auditResult.criticalFindings.slice(0, 3).map(f => f.actionable);
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getHealthScoreLabel = (score: number) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Fair";
    return "Needs Improvement";
  };

  return (
    <div className="space-y-6">
      <AuditLoadingBrain isOpen={isRunningAudit} progress={auditProgress} currentTask={currentTask} />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">AI CRM Audit</h2>
          <p className="text-muted-foreground">
            Deep business intelligence and performance analysis
          </p>
        </div>
        <Button
          onClick={runAudit}
          disabled={isRunningAudit}
          size="lg"
          className="gap-2"
        >
          <Brain className="h-5 w-5" />
          {isRunningAudit ? "Running Audit..." : "Run Full Audit"}
        </Button>
      </div>

      {auditHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Audit History
              </div>
              {selectedAuditId && selectedAuditId !== auditHistory[0]?.id && (
                <Button variant="outline" size="sm" onClick={backToLatest}>
                  Back to Latest
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {auditHistory.map((audit, idx) => (
                <div
                  key={audit.id}
                  onClick={() => !isLoadingAudit && loadAuditById(audit.id)}
                  className={`cursor-pointer flex items-center justify-between p-3 rounded-lg border transition-all ${
                    selectedAuditId === audit.id
                      ? 'border-primary bg-primary/5'
                      : 'bg-card hover:bg-accent/50 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {new Date(audit.created_at).toLocaleDateString()} at{" "}
                      {new Date(audit.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold ${getHealthScoreColor(audit.overall_score)}`}>
                      {audit.overall_score}
                    </span>
                    {idx === 0 && <Badge>Latest</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!auditResult && !isRunningAudit && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Brain className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Audit Data Yet</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Click "Run Full Audit" to get deep insights, identify critical issues, and create action plans
            </p>
          </CardContent>
        </Card>
      )}

      {auditResult && (
        <div className="space-y-6">
          {/* Critical Findings */}
          {auditResult.criticalFindings && auditResult.criticalFindings.length > 0 && (
            <CriticalFindingsAlert findings={auditResult.criticalFindings} />
          )}

          {/* Improve with AI Button */}
          {!actionPlan && (
            <Card className="border-2 border-primary/50 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="flex items-center justify-between p-6">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Ready to improve?
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    Get a personalized action plan based on your audit results
                  </p>
                </div>
                <Button
                  size="lg"
                  onClick={() => setShowGoalInput(true)}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Improve with AI
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Goal Input Section */}
          {showGoalInput && !actionPlan && (
            <Card>
              <CardHeader>
                <CardTitle>What do you want to improve?</CardTitle>
                <CardDescription>
                  Based on your audit, here are some suggested goals. Choose one or write your own.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {getSuggestedGoals().length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Suggested goals:</p>
                    <div className="flex flex-wrap gap-2">
                      {getSuggestedGoals().map((goal, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          size="sm"
                          onClick={() => setGoals(goal)}
                        >
                          {goal}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                <Textarea
                  placeholder="E.g., 'Speed up lead response time and generate 10 new qualified leads this month'"
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  rows={4}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={generateActionPlan}
                    disabled={isGeneratingPlan || !goals.trim()}
                    className="gap-2"
                  >
                    {isGeneratingPlan ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Generate Action Plan
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowGoalInput(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Plan Checklist */}
          {actionPlan && actionPlanId && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold">Your Action Plan</h3>
                <Badge variant="outline" className="text-lg px-4 py-2">
                  {planProgress}% Complete
                </Badge>
              </div>
              <ActionPlanChecklist
                plan={actionPlan}
                planId={actionPlanId}
                onProgressUpdate={(progress) => setPlanProgress(progress)}
              />
            </div>
          )}

          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="metrics">Detailed Metrics</TabsTrigger>
              <TabsTrigger value="insights">AI Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>CRM Health Score</span>
                    <span className={`text-5xl font-bold ${getHealthScoreColor(auditResult.healthScore)}`}>
                      {auditResult.healthScore}
                    </span>
                  </CardTitle>
                  <CardDescription>
                    {getHealthScoreLabel(auditResult.healthScore)} - Based on critical findings analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Progress value={auditResult.healthScore} className="h-3" />
                </CardContent>
              </Card>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${(auditResult.metrics?.revenue?.totalRevenue ?? 0).toLocaleString()}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Lost: ${(auditResult.metrics?.revenue?.lostRevenue ?? 0).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Response Time</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(auditResult.metrics?.performance?.avgResponseTime ?? 0).toFixed(1)}h
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Average lead response
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(auditResult.metrics?.performance?.conversionRate ?? 0).toFixed(1)}%
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Lead to customer
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Stale Leads</CardTitle>
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {auditResult.metrics?.pipeline?.staleLeads ?? 0}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      No contact in 7+ days
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Data Completeness</CardTitle>
                    <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(auditResult.metrics?.performance?.leadCompletenessScore ?? 0).toFixed(0)}%
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Lead data quality
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Task Completion</CardTitle>
                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(auditResult.metrics?.tasks?.taskCompletionRate ?? 0).toFixed(0)}%
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {auditResult.metrics?.tasks?.overdueTasks ?? 0} overdue
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="metrics" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Team Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {auditResult.metrics?.team?.totalMembers ?? 0} team members analyzed
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Pipeline Stages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {auditResult.metrics?.pipeline?.stages?.map((stage, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm font-medium capitalize">{stage.name}</span>
                        <Badge variant="outline">{stage.count} leads</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="insights" className="space-y-4">
              {auditResult.strengths.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      Strengths
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {auditResult.strengths.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-green-500 mt-1">✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {auditResult.weaknesses.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      Weaknesses
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {auditResult.weaknesses.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-red-500 mt-1">!</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {auditResult.opportunities.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-yellow-500" />
                      Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {auditResult.opportunities.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-yellow-500 mt-1">💡</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {auditResult.recommendations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-blue-500" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {auditResult.recommendations.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-blue-500 mt-1">→</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}